import { useScorecardTemplates } from './use-scorecard-templates';
import {
  Box,
  Center,
  Flex,
  Icon,
  Image,
  Spinner,
  Switch,
} from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import { LayoutLeft } from '@kula-ai/icons/dual-tone/LayoutLeft';
import scorecardIcon from '@/assets/svg/scorecard/scorecard-icon.svg';
import { FormBuilder } from '@/components/formBuilder';
import { components } from 'react-select';
import { AddQuestion } from './scorecard-builder/AddQuestion';
import { ScoreCardBuilder } from './scorecard-builder/ScorecardBuilder';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { ScheduleAPI } from '../api';
import { useKFlags } from '@/hooks/use-k-flags';
import {
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from 'react';
import { useScorecardVisibilitySettings } from '@/api/scorecard-visibility';
import { SchFillWithAiField } from '../SchedulerDetails/InterviewSchedulerForm/Fields/useSchLocationField/SchFillWithAiField';

const getScorecardPayload = (scorecards = []) => {
  return scorecards?.map((scorecard) => ({
    question: scorecard?.question,
    type: scorecard?.type,
    mandatory: scorecard?.mandatory || false,
    description: scorecard?.description || '',
    ...(scorecard?.options?.length
      ? {
          options: scorecard?.options?.map((option) => option?.optionText),
        }
      : {}),
  }));
};

interface ScorecardProps {
  interviewPlan?: any;
  note?: string;
  schedulerDetailsRef?: React.RefObject<any>;
  /** When true, scorecard step is visible; used to re-resolve Fill with AI context after Details form is filled (e.g. adhoc flow). */
  isScorecardStepActive?: boolean;
  toggleLabel?: string;
}
export const Scorecard = forwardRef(
  (
    {
      interviewPlan = {},
      note = 'This is the recommended scorecard template. You can customize it but changes will only apply to this interview.',
      schedulerDetailsRef,
      isScorecardStepActive = false,
      toggleLabel = 'Include scorecards in this interview',
    }: ScorecardProps,
    ref
  ) => {
    const formRef = useRef(null);
    const { currentJob: { data: currentJobData = {} } = {} } =
      useSelector(getAtsJobCreate);
    const { basic_info = {} } = currentJobData;
    const { department = {}, employment_type = '', office = [] } = basic_info;
    const defaultScorecardParams = {
      department_ids: [department?.id],
      employment_types: [employment_type],
      office_ids: office?.map((item) => item?.id),
    };
    const containerRef = useRef(null);
    const { feScorecardOptional } = useKFlags();
    const parseScorecardRequired = (value: unknown): boolean => {
      if (value === null || value === undefined) return true;
      return value !== false && value !== 'false';
    };
    const [scorecardRequired, setScorecardRequired] = useState(
      parseScorecardRequired(interviewPlan?.scorecard_required)
    );
    const [prevScorecardProp, setPrevScorecardProp] = useState(
      interviewPlan?.scorecard_required
    );
    if (interviewPlan?.scorecard_required !== prevScorecardProp) {
      setPrevScorecardProp(interviewPlan?.scorecard_required);
      setScorecardRequired(
        parseScorecardRequired(interviewPlan?.scorecard_required)
      );
    }
    const { scorecard_config = {} } = interviewPlan ?? {};
    const { data: defaultScoreCard, isLoading } = useQuery({
      queryKey: ['getDefaultScorecard', defaultScorecardParams],
      queryFn: () =>
        ScheduleAPI?.getDefaultScorecard({
          payload: defaultScorecardParams,
        }),
      enabled: !interviewPlan?.id,
    });

    const scorecard = {
      scorecard_config: interviewPlan?.scorecard_config,
      scorecard_rating_description:
        interviewPlan?.scorecard_rating_description ||
        scorecard_config?.rating_description,
      scorecard_feedback_description:
        interviewPlan?.scorecard_feedback_description ||
        scorecard_config?.feedback_description,
      is_feedback_mandatory:
        interviewPlan?.scorecard_config?.is_feedback_mandatory ??
        scorecard_config?.is_feedback_mandatory ??
        false,
    };

    const scorecardDets = interviewPlan?.id
      ? scorecard
      : defaultScoreCard?.questions?.length
        ? defaultScoreCard
        : [];

    const {
      templateOptions,
      initialValues,
      changeTemplateInProgress,
      handleTemplateOnChange,
    } = useScorecardTemplates(
      scorecardDets?.scorecard_config?.questions,
      scorecardDets
    );

    const { data: scorecardVisibilityData } = useScorecardVisibilitySettings();
    const fillWithAiAccountEnabled =
      scorecardVisibilityData?.data?.account_setting?.fill_with_ai_enabled;

    // Resolve form ref and visibility in effect to avoid "Cannot access ref value during render"
    const [fillWithAiContext, setFillWithAiContext] = useState<{
      formRef: React.RefObject<any>;
      updateSharedData: ((_: string, __: any) => void) | undefined;
      showBlock: boolean;
    } | null>(null);

    useEffect(() => {
      const resolveFillWithAiContext = () => {
        if (!schedulerDetailsRef || !fillWithAiAccountEnabled) return;
        const formRef = schedulerDetailsRef.current?.getFormRef?.();
        const updateSharedData =
          schedulerDetailsRef.current?.getUpdateSharedData?.();
        if (!formRef?.current?.values) return;
        const { interviewers, location, note_taker_enabled } =
          formRef.current.values ?? {};
        const isAtLeastOneInterviewer = Array.isArray(interviewers)
          ? interviewers?.length > 0
          : !!interviewers;
        const isValidLocation = ['zoom', 'google_meet', 'teams'].includes(
          location
        );
        const showBlock =
          isAtLeastOneInterviewer &&
          isValidLocation &&
          note_taker_enabled === true;
        setFillWithAiContext({
          formRef,
          updateSharedData: updateSharedData ?? undefined,
          showBlock,
        });
      };

      const id = setTimeout(resolveFillWithAiContext, 0);
      return () => clearTimeout(id);
    }, [
      schedulerDetailsRef,
      fillWithAiAccountEnabled,
      interviewPlan?.id,
      isScorecardStepActive,
    ]);

    const showFillWithAiBlock = fillWithAiContext?.showBlock ?? false;
    const schedulerFormRef = fillWithAiContext?.formRef;
    const updateSharedData = fillWithAiContext?.updateSharedData;

    const CustomValueContainer = ({ children, ...props }) => (
      <components.ValueContainer {...props}>
        <Flex alignItems="center" gap="4px">
          <Icon as={LayoutLeft} color="secondary-700" />
          <Box>{children}</Box>
        </Flex>
      </components.ValueContainer>
    );
    const formConfig = [
      {
        name: 'scorecard_template',
        type: 'kSelect',
        placeholder: 'Select scorecard template',
        options: templateOptions,
        components: {
          IndicatorSeparator: () => null,
          ValueContainer: CustomValueContainer,
        },
        customOnChange: handleTemplateOnChange,
      },
    ];

    const scrollToError = () => {
      setTimeout(() => {
        const errorElement = document.querySelector(
          '.chakra-form__error-message, [aria-invalid="true"]'
        );
        if (errorElement) {
          const container =
            errorElement.closest('[draggable="true"]') || errorElement;
          container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    };

    const getModel = async () => {
      if (!scorecardRequired) {
        return {
          hasValidData: true,
          // ENG-3207: send as string — BE coerces a JSON boolean `false` to `true`.
          payload: { scorecard_required: 'false' },
        };
      }

      await formRef.current?.validateForm();
      await new Promise((resolve) => setTimeout(resolve, 50));
      const isValid = formRef?.current?.isValid;

      if (!isValid) {
        scrollToError();
      }

      const {
        feedback_description,
        is_feedback_mandatory,
        rating_description,
        scoreCards,
      } = formRef?.current?.values || {};

      const scorecardPayload = getScorecardPayload(scoreCards);

      const payload = {
        scorecard_config: {
          questions: scorecardPayload,
          is_feedback_mandatory,
        },
        scorecard_feedback_description: feedback_description,
        scorecard_rating_description: rating_description,
        // ENG-3207: string to match the BE's string-boolean contract.
        scorecard_required: 'true',
      };
      return {
        hasValidData: isValid,
        payload,
      };
    };

    useImperativeHandle(ref, () => ({
      getModel,
    }));

    return (
      <Flex
        p="16px 24px"
        flexDir="column"
        overflowX="hidden"
        data-test-id="section-scorecard"
        ref={containerRef}
      >
        {feScorecardOptional && (
          <Flex
            alignItems="center"
            justifyContent="space-between"
            p="16px"
            bg="secondary-25"
            borderRadius="6px"
            mb={scorecardRequired ? '12px' : '0'}
          >
            <Flex alignItems="center" gap="12px">
              <Image
                src={scorecardIcon}
                alt=""
                width="35px"
                height="45px"
                flexShrink={0}
              />
              <KText size="sm-semi-bold" color="secondary-900">
                {toggleLabel}
              </KText>
            </Flex>
            <Switch
              className={`ats-switch-toogle ${scorecardRequired ? 'active' : ''}`}
              colorScheme="ats-primary"
              isChecked={scorecardRequired}
              onChange={(e) => setScorecardRequired(e.target.checked)}
            />
          </Flex>
        )}
        <Flex
          flexDir="column"
          gap="12px"
          h={scorecardRequired ? 'auto' : '0'}
          overflow={scorecardRequired ? 'visible' : 'hidden'}
        >
          {showFillWithAiBlock && schedulerFormRef && (
            <SchFillWithAiField
              schedulerFormRef={schedulerFormRef}
              updateSharedData={updateSharedData}
              interviewPlan={interviewPlan}
            />
          )}
          <FormBuilder config={formConfig} formRef={null}></FormBuilder>
          <KText
            borderWidth="1px"
            borderColor="secondary-100"
            borderRadius="xs"
            bg="secondary-25"
            p="12px"
          >
            <b>Note:</b>
            {note}
          </KText>
          <AddQuestion formRef={formRef} />
          {changeTemplateInProgress || isLoading ? (
            <Center h="50vh" flex="1">
              <Spinner />
            </Center>
          ) : (
            <ScoreCardBuilder
              key={JSON.stringify(initialValues)}
              formRef={formRef}
              initialValues={initialValues}
            />
          )}
        </Flex>
      </Flex>
    );
  }
);

Scorecard.displayName = 'Scorecard';
